import React, { FC } from 'react';
import CareTypeInfo from './CareTypeInfo';
import { isMobile } from '../../helpers';
import { VirtualVisitCareCard } from './VirtualVisitCareCard';
import { ClinicsAttachmentData } from '../';
import styles from './CareRecommendations.module.css';
import GenericCard, { GenericContentType } from '../GenericCard';
import { ReactComponent as OutlineInfoSvg } from '../../icons/outline_info.svg';
import careRecommendationsConfig from '../../resources/careRecommendationsConfig.json';

import { AmpFlagSet } from '../../amplitude';

const EMERGENCY_TEXT = careRecommendationsConfig.providence.emergency;
const EMERGENCY_LINK = isMobile() ? (
    <p className={styles.recommendationsEmergencyText}>
        {EMERGENCY_TEXT} <a href="tel:911">911</a>
    </p>
) : (
    <p className={styles.recommendationsEmergencyText}> {EMERGENCY_TEXT} 911</p>
);

export const VirtualCard: FC<{
    featureFlags: AmpFlagSet;
    template: any;
    virtualUrl: string;
    triage: boolean;
}> = ({ featureFlags, template, virtualUrl, triage }) => {
    const virtualEntryCard = <VirtualVisitCareCard template={template} />;
    const buttonInfo = {
        title: careRecommendationsConfig.providence.startVirtualVisit,
        url: virtualUrl,
        amplitudeEvent: {
            eventType: 'Care Options - Virtual Visit',
            eventProperties: {
                linkout_url: virtualUrl, // eslint-disable-line
            },
        },
    };
    const contentInfo: string = triage
        ? careRecommendationsConfig.providence.expresscareVirtualAbout
        : careRecommendationsConfig.providence.expresscareVirtualDescription;

    const dialogContent = (
        <>
            <CareTypeInfo
                title={careRecommendationsConfig.providence.expresscareVirtual}
                subtitle={virtualEntryCard}
            />
            <p>{EMERGENCY_LINK}</p>
        </>
    );

    const cardContent = [
        {
            type: GenericContentType.Info,
            info: { text: contentInfo },
            image: (
                <OutlineInfoSvg className={styles.recommendationsContentIcon} />
            ),
            accordionInfo: dialogContent,
        },
    ];

    return (
        <GenericCard
            data={{
                title: {
                    text: careRecommendationsConfig.providence
                        .expresscareVirtual,
                },
                subtitles: [
                    {
                        text: careRecommendationsConfig.providence
                            .expresscareVirtualTime,
                        disableTruncation: true,
                    },
                ],
                content: cardContent,
                buttonData: buttonInfo,
            }}
            featureFlags={featureFlags}
        />
    );
};

export const SameDayCareCard: FC<{
    data: ClinicsAttachmentData;
    featureFlags: AmpFlagSet;
}> = ({ data, featureFlags }) => {
    const urgentCareSearchUrl =
        'https://www.providence.org/our-services/urgent-care' +
        (data?.zip ? '?location=' + data.zip : '');

    const amplitudeEvent = {
        eventType: 'Care Options - EC & UC',
        eventProperties: {
            linkout_url: urgentCareSearchUrl, // eslint-disable-line
        },
    };

    const dialogContent = (
        <>
            <CareTypeInfo
                title={careRecommendationsConfig.providence.expresscareTitle}
                subtitle={careRecommendationsConfig.providence.expresscareInfo}
            />
            <CareTypeInfo
                title={careRecommendationsConfig.providence.urgentCareTitle}
                subtitle={careRecommendationsConfig.providence.urgentCareInfo}
            />
            <p>{EMERGENCY_LINK}</p>
        </>
    );

    const buttonInfo = {
        amplitudeEvent: data.isUnified ? amplitudeEvent : undefined,
        title: careRecommendationsConfig.providence.viewClinics,
        url: data.isUnified ? data.urgentCareUrl : urgentCareSearchUrl,
    };
    const cardContent = [
        {
            type: GenericContentType.Info,
            info: { text: careRecommendationsConfig.providence.chooseCare },
            image: (
                <OutlineInfoSvg className={styles.recommendationsContentIcon} />
            ),
            accordionInfo: dialogContent,
        },
    ];

    return (
        <GenericCard
            data={{
                title: {
                    text: careRecommendationsConfig.providence.sameDayCare,
                },
                content: cardContent,
                buttonData: buttonInfo,
            }}
            featureFlags={featureFlags}
        />
    );
};

const getSchedulePcpButtonData = (primaryCareUrl: string) => {
    const schedulePcpAptTitle =
        careRecommendationsConfig.providence.scheduleAppointment;
    return {
        subTitle: careRecommendationsConfig.providence.primaryCareProvider,
        buttonData: {
            amplitudeEvent: {
                eventType: `Care Options - PC - ${schedulePcpAptTitle}`,
                eventProperties: {
                    linkout_url: primaryCareUrl, // eslint-disable-line
                },
            },
            title: schedulePcpAptTitle,
            url: primaryCareUrl,
        },
        info: careRecommendationsConfig.providence.makeAppointmentWithPCP,
        onClick: () => undefined,
    };
};

const getFindPcpButtonData = (primaryCareUrl: string) => {
    const findPcpTitle = careRecommendationsConfig.providence.findProvider;
    return {
        subTitle: careRecommendationsConfig.providence.findProvider,
        buttonData: {
            amplitudeEvent: {
                eventType: `Care Options - PC - ${findPcpTitle}`,
                eventProperties: {
                    linkout_url: primaryCareUrl, // eslint-disable-line
                },
            },
            title: findPcpTitle,
            url: primaryCareUrl,
        },
        info: careRecommendationsConfig.providence.noProvider,
        onClick: () => undefined,
    };
};

export const PrimaryCareCard: FC<{
    data: ClinicsAttachmentData;
    featureFlags: AmpFlagSet;
}> = ({ data, featureFlags }) => {
    const { hasPcp, primaryCareUrl } = data;
    const dialogContent = (
        <>
            <CareTypeInfo
                title={careRecommendationsConfig.providence.primaryCare}
                subtitle={
                    careRecommendationsConfig.providence
                        .primaryCareProviderDescription
                }
            />
            <p>{EMERGENCY_LINK}</p>
        </>
    );

    const { subTitle, buttonData, info } = hasPcp
        ? getSchedulePcpButtonData(primaryCareUrl)
        : getFindPcpButtonData(primaryCareUrl);

    const cardContent = [
        {
            type: GenericContentType.Info,
            info: {
                text: info,
            },
            image: (
                <OutlineInfoSvg className={styles.recommendationsContentIcon} />
            ),
            accordionInfo: dialogContent,
        },
    ];

    return (
        <GenericCard
            data={{
                title: {
                    text: careRecommendationsConfig.providence.primaryCare,
                },
                subtitles: [
                    {
                        text: subTitle,
                        disableTruncation: true,
                    },
                ],
                content: cardContent,
                buttonData: buttonData,
            }}
            featureFlags={featureFlags}
        />
    );
};
